import BybVrCard from "@Components/common/byb-ds/cards/byb-vr-card";
import WeeklyTrainingTime from "@Components/game/progress/stats/weekly-training-time";
import { useProgress } from "@Context/game/progress/progress-context";
import useI18n from "@i18n";
import { Stack, Typography, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { Bar, BarChart, Cell, ResponsiveContainer, XAxis } from "recharts";

interface IWeeklyTraining {}

const CustomXAxisTick = (props: any) => {
  const { x, y, payload } = props;
  const theme = useTheme();

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle">
        <Typography component="tspan" variant="textS600" sx={{ fill: theme.palette.dsNeutral.v300 }}>
          {payload.value}
        </Typography>
      </text>
    </g>
  );
};

const WeeklyTraining = ({}: IWeeklyTraining) => {
  const { i18n } = useI18n();
  const theme = useTheme();
  const { weeklyDailyStats } = useProgress();

  const sessionDates = weeklyDailyStats.filter((session: any) => session.updated_at).map((session: any) => dayjs(session.updated_at));

  const startOfWeek =
    sessionDates.length > 0
      ? dayjs(sessionDates.reduce((min, date) => (date.isBefore(min) ? date : min), sessionDates[0])).startOf("week")
      : dayjs().startOf("week");

  const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

  const weekData = Array.from({ length: 7 }, (_, index) => {
    const currentDay = startOfWeek.add(index, "day");

    const daySessions = weeklyDailyStats.filter((session: any) => {
      if (!session.updated_at) return false;
      const sessionDate = dayjs(session.updated_at);
      return sessionDate.isSame(currentDay, "day");
    });

    const trainingMinutes = daySessions.reduce((total: number, session: any) => {
      const durationInSeconds = session.total_gameplay_duration || 0;
      return total + Math.round(durationInSeconds / 60);
    }, 0);

    return {
      day: daysOfWeek[index],
      minutes: trainingMinutes,
    };
  });

  const totalMinutes = weekData.reduce((sum, day) => sum + day.minutes, 0);

  return (
    <BybVrCard sx={{ width: "100%", height: "100%" }}>
      <Stack gap={1.5} width="100%" justifyContent="space-between" height="100%">
        <Stack gap={1} width="100%">
          <Typography variant="text1xl400" color="text.secondary" textAlign="left">
            {i18n("progress.training.weekly-training")}
          </Typography>
          <WeeklyTrainingTime totalMinutes={totalMinutes} />
        </Stack>

        <ResponsiveContainer width="100%" height={120}>
          <BarChart data={weekData} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={<CustomXAxisTick />} height={24} />
            <Bar dataKey="minutes" radius={[10, 10, 10, 10]} maxBarSize={15}>
              {weekData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={theme.palette.dsGreen?.v200} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Stack>
    </BybVrCard>
  );
};

export default WeeklyTraining;
